import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@vitness/theme';
import { MoreHorizontal } from '@vitness/icons';
import { vp } from '@vitness/tokens';
import { Tooltip } from './Tooltip.js';

export interface SidebarAccountProps {
  /**
   * Fully custom identity node (avatar + name), e.g. Clerk's `<UserButton showName />`.
   * When provided it replaces the built-in avatar + name + meta block; the
   * trailing 3-dot menu trigger is still rendered.
   */
  identity?: ReactNode;
  /**
   * Avatar-only identity for the collapsed icon rail (e.g. Clerk's
   * `<UserButton />` without `showName`). Falls back to `identity` when unset.
   */
  collapsedIdentity?: ReactNode;
  /** Primary line — the account / user / team name. Ignored when `identity` is set. */
  name?: ReactNode;
  /** Secondary mono line (e.g. `Pro · 2.4M req/mo`). Ignored when `identity` is set. */
  meta?: ReactNode;
  /** Initials for the default accent avatar. Ignored when `avatar`/`identity` is given. */
  initials?: string;
  /** Custom avatar node, overriding the default initials chip. */
  avatar?: ReactNode;
  /** Content rendered inside the popover (e.g. a ThemeSwitcher + actions). */
  menu?: ReactNode;
  /** Small mono heading shown at the top of the popover. */
  menuTitle?: ReactNode;
  /** Accessible label for the trigger. Defaults to `Account menu`. */
  menuAriaLabel?: string;
  /** Width of the popover in pixels. Defaults to 248. */
  menuWidth?: number;
  /** Collapsed rail mode — show only the avatar; it becomes the trigger. */
  collapsed?: boolean;
}

/**
 * Sidebar footer account chip: avatar + name + meta with a trailing 3-dot
 * trigger that opens an upward popover. The popover hosts arbitrary content —
 * typically a {@link ThemeSwitcher} plus account actions — so themes, settings,
 * and sign-out live together off the rail. Collapsed mode reduces to the avatar.
 */
export function SidebarAccount({
  identity,
  collapsedIdentity,
  name,
  meta,
  initials = '',
  avatar,
  menu,
  menuTitle,
  menuAriaLabel = 'Account menu',
  menuWidth = 248,
  collapsed = false,
}: SidebarAccountProps) {
  const { accent, accentRgb } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Anchor above the trigger, aligned to the sidebar's left padding.
    setPos({ bottom: window.innerHeight - rect.top + 8, left: collapsed ? rect.right + 12 : rect.left });
  }, [open, collapsed]);

  const avatarNode = avatar ?? (
    <span
      aria-hidden="true"
      style={{
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: accent,
        color: '#000',
        fontFamily: 'var(--vn-font-display)',
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      {initials}
    </span>
  );

  const popover =
    open && menu != null
      ? createPortal(
          <>
            <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
            <div
              role="menu"
              style={{
                position: 'fixed',
                bottom: pos.bottom,
                left: pos.left,
                zIndex: 9999,
                width: menuWidth,
                maxWidth: vp('100vw - 24px'),
                background: 'var(--vn-surface-card)',
                border: '1px solid var(--vn-border-strong)',
                borderRadius: 'var(--vn-radius-lg)',
                boxShadow: 'var(--vn-shadow-modal)',
                padding: 12,
              }}
            >
              {menuTitle != null && (
                <div
                  style={{
                    fontFamily: 'var(--vn-font-mono)',
                    fontSize: 8.5,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--vn-text-muted)',
                    marginBottom: 10,
                  }}
                >
                  {menuTitle}
                </div>
              )}
              {menu}
            </div>
          </>,
          document.body,
        )
      : null;

  if (collapsed) {
    // A custom identity (e.g. Clerk UserButton) is its own avatar-sized trigger.
    // Prefer an avatar-only node so the name doesn't clip on the narrow rail.
    const collapsedNode = collapsedIdentity ?? identity;
    if (collapsedNode != null) {
      return <div style={{ display: 'flex', justifyContent: 'center' }}>{collapsedNode}</div>;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip content={menuAriaLabel} side="right">
          <button
            ref={triggerRef}
            type="button"
            aria-label={menuAriaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 36,
              height: 36,
              padding: 0,
              border: 'none',
              borderRadius: 'var(--vn-radius-md)',
              background: open ? `rgba(${rs},0.08)` : 'transparent',
              cursor: 'pointer',
              transition: 'background 0.12s ease',
            }}
          >
            {avatarNode}
          </button>
        </Tooltip>
        {popover}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      {identity != null ? (
        <div style={{ minWidth: 0, flex: 1 }}>{identity}</div>
      ) : (
        <>
          {avatarNode}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--vn-text-primary)',
              }}
            >
              {name}
            </div>
            {meta != null && (
              <div
                style={{
                  fontFamily: 'var(--vn-font-mono)',
                  fontSize: 9,
                  color: 'var(--vn-text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {meta}
              </div>
            )}
          </div>
        </>
      )}
      {menu != null && (
        <button
          ref={triggerRef}
          type="button"
          aria-label={menuAriaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          style={{
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            width: 28,
            height: 28,
            border: 'none',
            borderRadius: 'var(--vn-radius-md)',
            background: open ? `rgba(${rs},0.08)` : 'transparent',
            color: 'var(--vn-text-muted)',
            cursor: 'pointer',
            transition: 'background 0.12s ease, color 0.12s ease',
          }}
          onMouseEnter={(e) => {
            if (!open) {
              e.currentTarget.style.background = `rgba(${rs},0.06)`;
              e.currentTarget.style.color = 'var(--vn-text-primary)';
            }
          }}
          onMouseLeave={(e) => {
            if (!open) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--vn-text-muted)';
            }
          }}
        >
          <MoreHorizontal size={14} strokeWidth={1.8} />
        </button>
      )}
      {popover}
    </div>
  );
}
